import {Component, OnDestroy, OnInit} from '@angular/core';
import {DecimalPipe, NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatTooltipModule} from '@angular/material/tooltip';
import {combineLatest, Subscription} from 'rxjs';

import {
  CriterionDef,
  CriterionGroup,
  EvaluationGame,
  EvaluationSeason,
  EvaluationState,
  GROUP_LABELS,
  GROUP_ORDER,
  STATE_LABELS,
} from '../../model/evaluation/evaluation';
import {EvaluationsService} from '../../services/evaluations.service';
import {effectiveValue} from '../reward';

/** One criterion against one production company, or against the whole season. */
interface Cell {
  games: number;
  /** Criteria with a value — measured, or ruled on by an admin. */
  decided: number;
  /** Still without one: the tool would not say and nobody has yet. */
  open: number;
  /** Sum of the values, so a 0.9 ruling counts as nine tenths of a game. */
  met: number;
  /** `met / decided`, or null when nothing here has been decided at all. */
  share: number | null;
  /** Raw sum, for the criterion whose value is a count rather than a verdict. */
  total: number;
}

interface CriterionRow {
  criterion: CriterionDef;
  /** First cell of a new group, so the table can print a heading before it. */
  groupStart: string | null;
  cells: Cell[];
  all: Cell;
}

/**
 * Every criterion against every production company, as one grid.
 *
 * The season overview answers "where is the work" — how many items are still
 * open and what has been approved. This answers the other question, the one
 * that comes up when a production company argues about a cut: *is this us, or
 * is it everybody?* A sponsor message that 40 % of the league misses is a
 * problem with the message; one that only Tempo misses is a problem with
 * Tempo, and the grid is the only place that difference is visible.
 *
 * Shares are of the **decided** games, not of all of them. Counting an
 * unmeasured criterion as a miss would put a production company's number down
 * for work nobody has looked at yet, which is exactly the accusation the tool
 * spends so much effort not making; the open ones are carried separately and
 * shown next to the share.
 */
@Component({
  selector: 'app-evaluation-criteria-overview',
  templateUrl: './criteria-overview.component.html',
  styleUrls: ['./criteria-overview.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    DecimalPipe,
    NgForOf,
    NgIf,
  ]
})
export class EvaluationCriteriaOverviewComponent implements OnInit, OnDestroy {

  readonly stateLabels = STATE_LABELS;
  readonly states: EvaluationState[] = ['toDecide', 'toApprove', 'returned', 'approved'];
  /** Its value is how many extra videos ran, so a percentage would be nonsense. */
  readonly countCriterion = 'extraVideos';

  season: EvaluationSeason;
  producers: string[] = [];
  gamesPerProducer: number[] = [];
  rows: CriterionRow[] = [];
  games = 0;

  stateFilter: EvaluationState | '' = '';

  private all: EvaluationGame[] = [];
  private subscriptions = new Subscription();

  constructor(private evaluations: EvaluationsService) {
  }

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([this.evaluations.season(), this.evaluations.games()])
        .subscribe(([season, games]) => {
          this.season = season;
          this.all = games ?? [];
          this.rebuild();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  rebuild(): void {
    if (!this.season) {
      return;
    }
    const games = this.all.filter(game => !this.stateFilter || game.state === this.stateFilter);
    this.games = games.length;
    // Every production company stays in the grid even when the state filter
    // leaves it with no games: a column that disappears reads as "nothing
    // wrong here" rather than "nothing to show".
    this.producers = [...new Set(this.all.map(game => game.producerName))].sort();
    const byProducer = this.producers.map(name => games.filter(game => game.producerName === name));
    this.gamesPerProducer = byProducer.map(list => list.length);

    const ordered = [...(this.season.criteria ?? [])]
      .sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
    let group: CriterionGroup | null = null;
    this.rows = ordered.map(criterion => {
      const groupStart = criterion.group === group ? null : GROUP_LABELS[criterion.group];
      group = criterion.group;
      return {
        criterion,
        groupStart,
        cells: byProducer.map(list => this.cell(criterion, list)),
        all: this.cell(criterion, games),
      };
    });
  }

  private cell(criterion: CriterionDef, games: EvaluationGame[]): Cell {
    let decided = 0;
    let met = 0;
    let total = 0;
    games.forEach(game => {
      const value = effectiveValue(criterion.id, game);
      if (value === null) {
        return;
      }
      decided += 1;
      met += Math.max(0, Math.min(1, value));
      total += value;
    });
    return {
      games: games.length,
      decided,
      open: games.length - decided,
      met,
      share: decided ? met / decided : null,
      total,
    };
  }

  /**
   * Green when it is essentially always met, red when it routinely is not —
   * but only where the share is a rate rather than a handful of games.
   *
   * Two rows would otherwise read as league-wide failures they are not. The
   * extra videos are a count, and nought of them is what the contract expects.
   * The operated camera is declared by the producer after the game reaches
   * them, so almost every game is open all season; a red nought there would
   * be about how few have been declared, not about the cameras.
   */
  tone(criterion: CriterionDef, cell: Cell): string {
    if (cell.share === null || criterion.id === this.countCriterion
        || cell.decided * 2 < cell.games) {
      return 'none';
    }
    if (cell.share >= 0.95) {
      return 'good';
    }
    return cell.share >= 0.7 ? 'fair' : 'bad';
  }

  hint(cell: Cell): string {
    if (!cell.games) {
      return 'No broadcasts';
    }
    const parts = [`${this.round(cell.met)} of ${cell.decided} met`];
    if (cell.open) {
      parts.push(`${cell.open} still open`);
    }
    return parts.join(' · ');
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
